import { canUseLocalStorage } from '../components/CookieConsent';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const SETTINGS_CACHE_KEY = 'mycinemas_settings';
const SETTINGS_CACHE_DURATION = 30 * 1000; // 30 seconds

// ─── Keys stripped before anything is cached or returned ─────────────────────
const SENSITIVE_KEYS = [
  'razorpayKeySecret',
  'easebuzzKey',
  'easebuzzSalt',
  'easebuzzWebhookSecret',
  'vistaApiUrl',
  'vistaApiKey',
  'vistaApiSecret',
  'brevoApiKey',   // stripped even if backend still sends it
  'zeptoApiKey',
  'smsApiKey',
  'allowedIpAddresses',
  'maxLoginAttempts',
  'passwordExpiry',
  'logLevel',
  'enableLogging',
  'enableBackup',
  'backupFrequency',
  'enableIpWhitelist',
];

const sanitize = (data) => {
  if (!data || typeof data !== 'object') return {};
  const safe = { ...data };
  for (const key of SENSITIVE_KEYS) {
    delete safe[key];
  }
  return safe;
};

// ─── Safe defaults — no secrets ───────────────────────────────────────────────
const DEFAULT_SETTINGS = {
  // General
  siteName:        'MyCinemas',
  siteDescription: "Kerala's Premier Cinema Experience",
  timezone:        'Asia/Kolkata',
  currency:        'INR',
  language:        'en',
  contactEmail:    'support@mycinemas.com',
  contactPhone:    '+91-484-1234567',
  address:         'MG Road, Kochi, Kerala 682001',

  // Booking
  maxSeatsPerBooking:   8,
  bookingTimeout:       15,
  cancellationDeadline: 2,
  refundProcessingDays: 3,
  enableSeatSelection:  true,
  enableAdvanceBooking: true,
  advanceBookingDays:   30,

  // Payment — public identifiers only, no secrets
  paymentGateway:       'razorpay',
  razorpayKeyId:        '',   // populated from API, not hardcoded
  paymentTimeout:       10,
  enableUPI:            true,
  enableCards:          true,
  enableNetBanking:     true,
  enableWallet:         true,
  enableCashOnDelivery: false,

  // Convenience fee
  enableConvenienceFee: false,
  convenienceFeeType:   'fixed',
  convenienceFeeValue:  0,

  // Cinema features
  enableOnlineBooking:    true,
  enableWalkInBooking:    true,
  enableFoodOrdering:     true,
  enableLoyaltyProgram:   true,
  loyaltyPointsPerRupee:  1,
  enableMembership:       true,
  membershipDiscount:     10,

  // Zepto sender display info only — no API key
  zeptoSenderEmail: 'noreply@mycinemas.in',
  zeptoSenderName:  'MyCinemas',
  whatsappBusinessNumber: '+918484123456',
  whatsappBusinessName:   'MyCinemas',

  // F&B
  fnbGst: 5,
};

// ─── Cache helpers (always store sanitized data) ──────────────────────────────
const loadSettingsFromCache = () => {
  if (!canUseLocalStorage()) return null;
  try {
    const cached = localStorage.getItem(SETTINGS_CACHE_KEY);
    if (cached) {
      const { settings, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < SETTINGS_CACHE_DURATION) return settings;
      localStorage.removeItem(SETTINGS_CACHE_KEY);
    }
  } catch {
    localStorage.removeItem(SETTINGS_CACHE_KEY);
  }
  return null;
};

const saveSettingsToCache = (settings) => {
  if (!canUseLocalStorage()) return;
  try {
    // Always sanitize before writing to cache
    localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify({
      settings: sanitize(settings),
      timestamp: Date.now(),
    }));
  } catch (err) {
    console.error('Error saving settings to cache:', err);
  }
};

// ─── Service ──────────────────────────────────────────────────────────────────
const settingsService = {
  getSettings: async (forceRefresh = false) => {
    try {
      if (!forceRefresh) {
        const cachedSettings = loadSettingsFromCache();
        if (cachedSettings) {
          return { success: true, data: cachedSettings, cached: true };
        }
      }

      const response = await fetch(`${API_BASE_URL}/settings`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();

      if (data.success && data.data) {
        const safe = sanitize(data.data);   // ✅ strip before cache + return
        saveSettingsToCache(safe);
        return { success: true, data: safe };
      } else {
        return { success: false, data: DEFAULT_SETTINGS, error: data.error };
      }
    } catch (err) {
      const cachedSettings = loadSettingsFromCache();
      if (cachedSettings) {
        return { success: true, data: cachedSettings, cached: true };
      }
      return { success: false, data: DEFAULT_SETTINGS, error: err.message };
    }
  },

  getBookingSettings: async (forceRefresh = false) => {
    const { data } = await settingsService.getSettings(forceRefresh);
    return {
      maxSeatsPerBooking:   data.maxSeatsPerBooking   ?? DEFAULT_SETTINGS.maxSeatsPerBooking,
      bookingTimeout:       data.bookingTimeout       ?? DEFAULT_SETTINGS.bookingTimeout,
      cancellationDeadline: data.cancellationDeadline ?? DEFAULT_SETTINGS.cancellationDeadline,
      refundProcessingDays: data.refundProcessingDays ?? DEFAULT_SETTINGS.refundProcessingDays,
      enableSeatSelection:  data.enableSeatSelection  ?? DEFAULT_SETTINGS.enableSeatSelection,
      enableAdvanceBooking: data.enableAdvanceBooking ?? DEFAULT_SETTINGS.enableAdvanceBooking,
      advanceBookingDays:   data.advanceBookingDays   ?? DEFAULT_SETTINGS.advanceBookingDays,
    };
  },

  getPaymentSettings: async (forceRefresh = false) => {
    const { data } = await settingsService.getSettings(forceRefresh);
    return {
      paymentGateway:       data.paymentGateway       || DEFAULT_SETTINGS.paymentGateway,
      razorpayKeyId:        data.razorpayKeyId        || DEFAULT_SETTINGS.razorpayKeyId,
      paymentTimeout:       data.paymentTimeout       ?? DEFAULT_SETTINGS.paymentTimeout,
      enableUPI:            data.enableUPI            ?? DEFAULT_SETTINGS.enableUPI,
      enableCards:          data.enableCards          ?? DEFAULT_SETTINGS.enableCards,
      enableNetBanking:     data.enableNetBanking     ?? DEFAULT_SETTINGS.enableNetBanking,
      enableWallet:         data.enableWallet         ?? DEFAULT_SETTINGS.enableWallet,
      enableCashOnDelivery: data.enableCashOnDelivery ?? DEFAULT_SETTINGS.enableCashOnDelivery,
      enableConvenienceFee: data.enableConvenienceFee ?? DEFAULT_SETTINGS.enableConvenienceFee,
      convenienceFeeType:   data.convenienceFeeType   || DEFAULT_SETTINGS.convenienceFeeType,
      convenienceFeeValue:  data.convenienceFeeValue  ?? DEFAULT_SETTINGS.convenienceFeeValue,
      // ⚠️ No secrets here — razorpayKeySecret etc. are backend-only
    };
  },

  getGeneralSettings: async (forceRefresh = false) => {
    const { data } = await settingsService.getSettings(forceRefresh);
    return {
      siteName:        data.siteName        || DEFAULT_SETTINGS.siteName,
      siteDescription: data.siteDescription || DEFAULT_SETTINGS.siteDescription,
      timezone:        data.timezone        || DEFAULT_SETTINGS.timezone,
      currency:        data.currency        || DEFAULT_SETTINGS.currency,
      language:        data.language        || DEFAULT_SETTINGS.language,
      contactEmail:    data.contactEmail    || DEFAULT_SETTINGS.contactEmail,
      contactPhone:    data.contactPhone    || DEFAULT_SETTINGS.contactPhone,
      address:         data.address         || DEFAULT_SETTINGS.address,
    };
  },

  // Zepto sender display info only — no API key
  getNotificationSettings: async (forceRefresh = false) => {
    const { data } = await settingsService.getSettings(forceRefresh);
    return {
      emailSenderName:  data.zeptoSenderName        || DEFAULT_SETTINGS.zeptoSenderName,
      emailSenderEmail: data.zeptoSenderEmail        || DEFAULT_SETTINGS.zeptoSenderEmail,
      whatsappName:     data.whatsappBusinessName    || DEFAULT_SETTINGS.whatsappBusinessName,
      whatsappNumber:   data.whatsappBusinessNumber  || DEFAULT_SETTINGS.whatsappBusinessNumber,
    };
  },

  clearCache: () => {
    if (canUseLocalStorage()) {
      localStorage.removeItem(SETTINGS_CACHE_KEY);
    }
  },

  getDefaultSettings: () => ({ ...DEFAULT_SETTINGS }),
};

export default settingsService;